import { ZipAvailabilityProbabilityEnum } from '@inter-ikea-kompis/enums';
import type { IKompisTranslations, IZipAvailability } from '@inter-ikea-kompis/types';

import type { StatusColour } from '@ingka/status-webc';

/** @deprecated Use translations settings. */
type IZipAvailabilityLocalizedInformation = {
	translations: IZipAvailabilityTranslations;
};

type IZipAvailabilityTranslations = Pick<
	IKompisTranslations,
	| 'homeDeliveryStatusSomeAvailableWithoutZip'
	| 'zipAvailabilityNoDeliveryInfo'
	| 'zipAvailabilityAvailable'
	| 'zipAvailabilityUnavailable'
	| 'zipAvailabilityCurrentlyNotSold'
	| 'zipAvailabilityNotSold'
>;

/**
 * Zip availability utility.
 */
export default class ZipAvailabilityUtility {
	/**
	 * Returns status indicator label for zip.
	 *
	 * @param options Options.
	 * @param options.localizedInformation Deprecated - Use translations instead.
	 * @param options.zipAvailability Zip availability.
	 * @param options.translations Translations.
	 * @returns Status Indicator Label.
	 */
	public static getLabel(options: {
		/** @deprecated Use translations instead. */
		localizedInformation?: IZipAvailabilityLocalizedInformation;
		zipAvailability: IZipAvailability | IZipAvailability[] | null;
		translations?: IZipAvailabilityTranslations;
	}): string {
		const { zipAvailability } = options;
		const actualTranslations = options.translations ?? options.localizedInformation?.translations;

		if (!actualTranslations) {
			throw Error(
				'Either translations or localizedInformation parameter must be set to calculate a label.'
			);
		}

		const { highInStock, lowInStock, mediumInStock, outOfStock, notSoldTemp, notInRange } =
			ZipAvailabilityProbabilityEnum;

		if (!Array.isArray(zipAvailability)) {
			if (!zipAvailability) {
				return actualTranslations.zipAvailabilityNoDeliveryInfo;
			}

			if (zipAvailability?.content?.availableForHomeDelivery) {
				return actualTranslations.zipAvailabilityAvailable;
			} else {
				switch (zipAvailability?.content?.homeDelivery?.messageType) {
					case outOfStock:
						return actualTranslations.zipAvailabilityUnavailable;
					case notSoldTemp:
						return actualTranslations.zipAvailabilityCurrentlyNotSold;
					case notInRange:
						return actualTranslations.zipAvailabilityNotSold;
					default:
						return actualTranslations.zipAvailabilityNoDeliveryInfo;
				}
			}
		} else {
			const zipAvailabilities = zipAvailability
				.map((item) => item.content.homeDelivery?.messageType)
				.filter((type) => type !== null);

			if (zipAvailabilities.every((type) => type === highInStock)) {
				return actualTranslations.zipAvailabilityAvailable;
			}
			if (zipAvailabilities.every((type) => type === outOfStock)) {
				return actualTranslations.zipAvailabilityUnavailable;
			} else if (
				zipAvailabilities.some(
					(type) => type === lowInStock || type === mediumInStock || type === outOfStock
				)
			) {
				return actualTranslations.homeDeliveryStatusSomeAvailableWithoutZip;
			}
		}
		return '';
	}

	/**
	 * Returns status indicator color for zip.
	 *
	 * @returns Status Indicator Color.
	 */
	public static getColor(options: {
		zipAvailability: IZipAvailability | IZipAvailability[] | null;
	}): StatusColour | null {
		const { zipAvailability } = options;
		const { highInStock, lowInStock, mediumInStock, outOfStock } = ZipAvailabilityProbabilityEnum;

		if (!Array.isArray(zipAvailability)) {
			if (!zipAvailability) {
				return 'grey';
			}

			if (zipAvailability?.content?.availableForHomeDelivery) {
				return 'green';
			} else {
				switch (zipAvailability.content?.homeDelivery?.messageType) {
					case ZipAvailabilityProbabilityEnum.outOfStock:
					case ZipAvailabilityProbabilityEnum.notSoldTemp:
						return 'red';
					default:
						return 'grey';
				}
			}
		} else {
			const zipAvailabilities = zipAvailability
				.map((item) => item.content.homeDelivery?.messageType)
				.filter((type) => !!type);

			// Is it intended that empty zipAvailabilities should return green?
			if (zipAvailabilities.every((type) => type === highInStock)) {
				return 'green';
			}
			if (zipAvailabilities.every((type) => type === outOfStock)) {
				return 'red';
			} else if (
				zipAvailabilities.some(
					(type) => type === lowInStock || type === mediumInStock || type === outOfStock
				)
			) {
				return 'orange';
			}
		}
		return null;
	}
}
